import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { queryHelpers, realtimeHelpers } from '../lib/supabase';

export const useUserBookings = (onBookingUpdate = null) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const subscriptionRef = useRef(null);

  const { userProfile, loading: authLoading } = useAuth();

  useEffect(() => {
    const fetchBookings = async () => {
      if (!userProfile || authLoading) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: bookingsData, error: bookingsError } = await queryHelpers?.getBookingsForUser(userProfile);
        
        if (bookingsError) {
          throw new Error(bookingsError);
        }

        setBookings(bookingsData || []);
      } catch (error) {
        console.error('Error fetching user bookings:', error);
        setError(error?.message || 'Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [userProfile, authLoading]);

  // Set up real-time subscription for bookings
  useEffect(() => {
    if (!userProfile || authLoading) {
      return;
    }

    // Clean up existing subscription
    if (subscriptionRef?.current) {
      subscriptionRef?.current?.unsubscribe();
      subscriptionRef.current = null;
    }

    // Set up new subscription
    const subscription = realtimeHelpers?.subscribeToBookings(userProfile, (payload) => {
      console.log('Real-time booking update:', payload);
      
      setBookings(currentBookings => {
        const { eventType, new: newRecord, old: oldRecord } = payload;
        
        switch (eventType) {
          case 'INSERT':
            // Add new booking
            if (onBookingUpdate) {
              onBookingUpdate(newRecord?.id, 'INSERT');
            }
            return [newRecord, ...currentBookings];
            
          case 'UPDATE':
            // Update existing booking
            if (onBookingUpdate) {
              onBookingUpdate(newRecord?.id, 'UPDATE');
            }
            return currentBookings?.map(booking => 
              booking?.id === newRecord?.id ? { ...booking, ...newRecord } : booking
            );
            
          case 'DELETE':
            // Remove deleted booking
            if (onBookingUpdate) {
              onBookingUpdate(oldRecord?.id, 'DELETE');
            }
            return currentBookings?.filter(booking => booking?.id !== oldRecord?.id);
            
          default:
            return currentBookings;
        }
      });
    });

    if (subscription) {
      subscriptionRef.current = subscription;
      setIsRealtimeConnected(true);
      
      // Handle subscription status
      subscription?.subscribe((status) => {
        console.log('Subscription status:', status);
        if (status === 'SUBSCRIBED') {
          setIsRealtimeConnected(true);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setIsRealtimeConnected(false);
        }
      });
    }

    // Cleanup function
    return () => {
      if (subscriptionRef?.current) {
        subscriptionRef?.current?.unsubscribe();
        subscriptionRef.current = null;
        setIsRealtimeConnected(false);
      }
    };
  }, [userProfile, authLoading]);

  // Helper functions
  const getUpcomingBookings = () => {
    const now = new Date();
    return bookings?.filter(booking => {
      const eventDate = booking?.events?.event_date ? new Date(booking.events.event_date) : null;
      return eventDate && eventDate > now;
    });
  };

  const getPastBookings = () => {
    const now = new Date();
    return bookings?.filter(booking => {
      const eventDate = booking?.events?.event_date ? new Date(booking.events.event_date) : null;
      return eventDate && eventDate <= now;
    });
  };

  const getBookingsByStatus = (status) => {
    return bookings?.filter(booking => booking?.booking_status === status);
  };

  const getBookingById = (bookingId) => {
    return bookings?.find(booking => booking?.id === bookingId);
  };

  const getBookingByReference = (reference) => {
    return bookings?.find(booking => booking?.booking_reference === reference);
  };

  const refreshBookings = async () => {
    if (!userProfile) return;
    
    try {
      setLoading(true);
      setError(null);

      const { data: bookingsData, error: bookingsError } = await queryHelpers?.getBookingsForUser(userProfile);
      
      if (bookingsError) {
        throw new Error(bookingsError);
      }

      setBookings(bookingsData || []);
    } catch (error) {
      console.error('Error refreshing bookings:', error);
      setError(error?.message || 'Failed to refresh bookings');
    } finally {
      setLoading(false);
    }
  };

  return {
    bookings,
    loading,
    error,
    isRealtimeConnected,
    getUpcomingBookings,
    getPastBookings,
    getBookingsByStatus,
    getBookingById,
    getBookingByReference,
    refreshBookings
  };
};

export default useUserBookings;
